import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { UserService } from '@core/services/user.service';
import { U2bUserApiService } from '@core/services/api/u2b-user.api-service';
import { U2bFilesApiService } from '@core/services/api/u2b-files.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { SalutationNew } from '@shared/models/salutation';
import { IFile } from '@shared/interfaces/file';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'my-profile-dialog',
    templateUrl: './my-profile-dialog.component.html',
    styleUrls: ['./my-profile-dialog.component.scss'],
    standalone: false
})
export class MyProfileDialogComponent implements OnInit {

    readonly salutationOptions = [
        {value: SalutationNew.None, label: 'Keine Angabe'},
        {value: SalutationNew.Male, label: 'Herr'},
        {value: SalutationNew.Female, label: 'Frau'},
        {value: SalutationNew.Divers, label: 'Divers'},
    ];

    form: UntypedFormGroup;
    isSaving = false;
    isUploadingImage = false;

    avatarPreviewUrl: SafeUrl | null = null;
    currentImage: IFile | null = null;

    get user() {
        return this.userService.user;
    }

    get avatarPlaceholder(): string {
        return this.user?.salutation === SalutationNew.Female
            ? 'assets/images/placeholder/female.jpg'
            : 'assets/images/placeholder/male.jpg';
    }

    constructor(
        public dialogRef: MatDialogRef<MyProfileDialogComponent>,
        private fb: UntypedFormBuilder,
        public userService: UserService,
        private userApiService: U2bUserApiService,
        private u2bFilesApiService: U2bFilesApiService,
        private notificationService: AppNotificationService,
        private domSanitizer: DomSanitizer,
    ) {}

    ngOnInit(): void {
        const u = this.user;
        this.currentImage = u?.image ?? null;

        this.form = this.fb.group({
            salutation: [u?.salutation ?? SalutationNew.None],
            title: [u?.title ?? ''],
            firstName: [u?.firstName ?? '', Validators.required],
            lastName: [u?.lastName ?? '', Validators.required],
            email: [{value: u?.email ?? '', disabled: true}],
            email2: [u?.email2 ?? ''],
            phone: [u?.phone ?? ''],
            mobilePhone: [u?.mobilePhone ?? ''],
            street: [u?.street ?? ''],
            postcode: [u?.postcode ?? ''],
            city: [u?.city ?? ''],
            country: [u?.country ?? ''],
            birthDate: [u?.birthDate ? new Date(u.birthDate) : null],
        });
    }

    onAvatarClick(fileInput: HTMLInputElement): void {
        fileInput.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) {
            return;
        }

        const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedMimes.includes(file.type)) {
            this.notificationService.showError('Nur JPEG, PNG, WebP oder GIF erlaubt.');
            return;
        }

        const maxBytes = 5 * 1024 * 1024;
        if (file.size > maxBytes) {
            this.notificationService.showError('Die Datei ist zu groß (max. 5 MB).');
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        this.avatarPreviewUrl = this.domSanitizer.bypassSecurityTrustUrl(objectUrl);

        this.isUploadingImage = true;
        this.u2bFilesApiService.upload(file).subscribe({
            next: (uploadedFile) => {
                this.currentImage = uploadedFile;
                this.isUploadingImage = false;
            },
            error: () => {
                this.notificationService.showError('Profilbild konnte nicht hochgeladen werden.');
                this.isUploadingImage = false;
                this.avatarPreviewUrl = null;
            },
        });
    }

    removeAvatar(): void {
        this.currentImage = null;
        this.avatarPreviewUrl = null;
    }

    save(): void {
        if (this.form.invalid || this.isSaving) {
            return;
        }

        this.isSaving = true;
        const formValue = this.form.getRawValue();

        const dto = {
            salutation: formValue.salutation,
            title: formValue.title || null,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            email2: formValue.email2 || null,
            phone: formValue.phone || null,
            mobilePhone: formValue.mobilePhone || null,
            street: formValue.street || null,
            postcode: formValue.postcode || null,
            city: formValue.city || null,
            country: formValue.country || null,
            birthDate: formValue.birthDate || null,
            image: this.currentImage,
        };

        this.userApiService.updateCurrent(dto).subscribe({
            next: (updatedUser) => {
                this.userService.user = updatedUser;
                this.notificationService.showSuccess('Profil gespeichert.');
                this.isSaving = false;
                this.dialogRef.close(updatedUser);
            },
            error: () => {
                this.notificationService.showError('Profil konnte nicht gespeichert werden.');
                this.isSaving = false;
            },
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
